import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface BusinessParameterInfo {
  paramId: string;
  businessType: string;
  level: number;
  requirements: {
    jion: number;
    freshWater: number;
    cogsValue: number;
    components: Record<string, number>;
    foodValue: number;
  };
  rewards: {
    mode: string;
    heptals: number;
    bonusItem: string | null;
    bonusCount: number;
  };
  creationCost: {
    heptals: number;
    spentCoinName: string;
  };
}

interface UseBusinessParametersReturn {
  parameters: BusinessParameterInfo[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBusinessParameters(type?: string): UseBusinessParametersReturn {
  const [parameters, setParameters] = useState<BusinessParameterInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchParams = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getBusinessParameters(type);
      if (fetchIdRef.current !== id) return;
      setParameters(result.parameters);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load parameters');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [type]);

  useEffect(() => {
    fetchParams();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchParams]);

  return { parameters, loading, error, refresh: fetchParams };
}
